import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getData, putData } from '../Services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const EditPO = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [values, setValues] = useState({
        po_number: '',
        po_type: '',
        customer_name: '',
        date: '',
        time: '',
        status: 'checking',
        comments: ''
    });
    
    const [productsList, setProductsList] = useState([]); // from product master
    const [customersList, setCustomersList] = useState([]); // from customer master
    const [poTypesList, setPoTypesList] = useState([]); // from po types master
    const [orderProducts, setOrderProducts] = useState([]); // products in this PO
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const isEditable = values.status !== 'invoiced' && values.status !== 'cancelled';

    // Fetch all initial data
    const fetchAllData = async () => {
        try {
            // 1. Fetch product master
            const prodRes = await getData('/product-master');
            let fetchedProducts = [];
            if (prodRes.status) {
                fetchedProducts = prodRes.data || [];
                setProductsList(fetchedProducts);
            }

            // 2. Fetch Order Details
            const res = await getData(`/proforma-orders/details/${id}`);
            if (res.status) {
                const orderData = res.data;
                setValues({
                    po_number: orderData.po_number || '',
                    po_type: orderData.po_type || '',
                    customer_name: orderData.customer_name || '',
                    date: orderData.date || '',
                    time: orderData.time || '',
                    status: orderData.status || 'checking',
                    comments: orderData.comments || ''
                });

                let prods = [];
                try {
                    prods = typeof orderData.products === 'string' 
                        ? JSON.parse(orderData.products) 
                        : (orderData.products || []);
                } catch (e) {
                    console.error("Failed to parse products in order", e);
                }
                
                // Map to UI representation
                setOrderProducts(prods.map((p, idx) => {
                    const matchedProd = fetchedProducts.find(x => x.product_name === (p.product || p.name));
                    return {
                        keyId: Date.now() + idx + Math.random(), // Unique key for react loop
                        product: p.product || p.name || '',
                        code: p.code || '',
                        qty: p.qty || p.quantity || 0,
                        unit: p.unit || '',
                        packing_unit_options: matchedProd ? (matchedProd.packing_unit || '') : '',
                        rate: p.rate || p.price || 0,
                        amount: p.amount || ((p.qty || p.quantity || 0) * (p.rate || p.price || 0))
                    };
                }));
            } else {
                toast.error("Proforma Order details not found");
            }

            // 3. Fetch customers list
            const custRes = await getData('/customers/all?limit=1000');
            if (custRes.success) {
                setCustomersList(custRes.data || []);
            }

            // 4. Fetch PO Types
            const poTypesRes = await getData('/po-types');
            if (poTypesRes.status) {
                setPoTypesList(poTypesRes.data || []);
            }

        } catch (error) {
            console.error("Error loading edit PO data:", error);
            toast.error("Failed to load PO information");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    // Products dynamic row actions
    const handleAddRow = () => {
        if (orderProducts.length >= 10) {
            toast.error("Product addition limit is up to 10 items only.");
            return;
        }
        setOrderProducts([
            ...orderProducts,
            {
                keyId: Date.now() + Math.random(),
                product: '',
                code: '',
                qty: 1,
                unit: '',
                rate: 0,
                amount: 0
            }
        ]);
    };

    const handleRemoveRow = (index) => {
        const updated = orderProducts.filter((_, i) => i !== index);
        setOrderProducts(updated);
    };

    const handleProductChange = (index, prodName) => {
        const selectedProd = productsList.find(p => p.product_name === prodName);
        const updated = [...orderProducts];
        
        if (selectedProd) {
            updated[index].product = selectedProd.product_name;
            updated[index].code = selectedProd.product_code || '';
            const pUnit = selectedProd.packing_unit || '';
            updated[index].packing_unit_options = pUnit;
            const units = pUnit.split(',').map(u => u.trim()).filter(u => u);
            updated[index].unit = units.length > 0 ? units[0] : '';
            // If rate isn't set, default to 0 (or some default value if available in DB)
            updated[index].rate = updated[index].rate || 0; 
            updated[index].amount = updated[index].qty * updated[index].rate;
        } else {
            updated[index].product = prodName;
        }
        setOrderProducts(updated);
    };

    const handleRowValChange = (index, field, val) => {
        const updated = [...orderProducts];
        updated[index][field] = val;
        
        if (field === 'qty' || field === 'rate') {
            const q = parseFloat(updated[index].qty) || 0;
            const r = parseFloat(updated[index].rate) || 0;
            updated[index].amount = parseFloat((q * r).toFixed(2));
        }
        setOrderProducts(updated);
    };

    // Sum total PO amount
    const calculateGrandTotal = () => {
        return orderProducts.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Simple validations
        if (!values.customer_name) {
            toast.error("Customer name is required");
            return;
        }

        if (orderProducts.length === 0) {
            toast.error("Please add at least one product item");
            return;
        }

        // Check if any product is not selected
        const incomplete = orderProducts.some(p => !p.product);
        if (incomplete) {
            toast.error("Please select a product for all rows");
            return;
        }

        Swal.fire({
            title: "Save Changes?",
            text: "Are you sure you want to update this Proforma Order?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, save it!",
            cancelButtonText: "Cancel"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setSaving(true);
                try {
                    // Prepare data payload
                    const payload = {
                        ...values,
                        products: orderProducts.map(p => ({
                            product: p.product,
                            code: p.code,
                            qty: Number(p.qty),
                            unit: p.unit,
                            rate: Number(p.rate),
                            amount: Number(p.amount)
                        }))
                    };

                    const res = await putData(`/proforma-orders/update/${id}`, payload);
                    if (res.status) {
                        toast.success("Proforma Order updated successfully!");
                        navigate('/accounts-dashboard/view-edit');
                    } else {
                        toast.error(res.message || "Failed to update Proforma Order");
                    }
                } catch (error) {
                    console.error("Save PO error:", error);
                    toast.error(error.response?.data?.message || "Internal server error");
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="pc-container">
                <div className="pc-content text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3 text-muted">Loading Proforma Order edit form...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="pc-container">
            <div className="pc-content">
                {/* Breadcrumbs */}
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/accounts-dashboard/view-edit">Accounts</Link></li>
                                    <li className="breadcrumb-item" aria-current="page">Edit PO</li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title">
                                    <h2 className="mb-0">Edit Proforma Order</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit Form */}
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        {/* PO Header Info Card */}
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5>PO Header Details</h5>
                                    {values.status === 'invoiced' && (
                                        <span className="badge bg-warning text-dark border">
                                            <i className="fas fa-exclamation-triangle me-1" /> PO ALREADY INVOICED
                                        </span>
                                    )}
                                    {values.status === 'cancelled' && (
                                        <span className="badge bg-danger text-white border">
                                            <i className="fas fa-times-circle me-1" /> PO CANCELLED
                                        </span>
                                    )}
                                </div>
                                <div className="card-body">
                                    {!isEditable && (
                                        <div className={`alert ${values.status === 'invoiced' ? 'alert-warning' : 'alert-danger'} py-2 mb-4 fs-6`}>
                                            <i className="fas fa-info-circle me-2" />
                                            <strong>Notice:</strong> This Proforma Order has been {values.status === 'invoiced' ? 'invoiced' : 'cancelled'}. It is now read-only and cannot be modified.
                                        </div>
                                    )}
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label">PO Number <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="po_number"
                                                value={values.po_number}
                                                onChange={handleInputChange}
                                                required
                                                disabled
                                            />
                                        </div>
                                        
                                        <div className="col-md-4">
                                            <label className="form-label">PO Type</label>
                                            <select
                                                className="form-select"
                                                name="po_type"
                                                value={values.po_type}
                                                onChange={handleInputChange}
                                                disabled={!isEditable}
                                            >
                                                <option value="">Select PO Type</option>
                                                {poTypesList.map(type => (
                                                    <option key={type.id} value={type.type_name}>{type.type_name}</option>
                                                ))}
                                                {/* Fallback standard types */}
                                                {!poTypesList.some(t => t.type_name === '2020') && <option value="2020">2020</option>}
                                                {!poTypesList.some(t => t.type_name === '5050') && <option value="5050">5050</option>}
                                            </select>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Customer Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="customer_name"
                                                list="customers-datalist"
                                                placeholder="Select or type customer name"
                                                value={values.customer_name}
                                                onChange={handleInputChange}
                                                required
                                                disabled
                                            />
                                            <datalist id="customers-datalist">
                                                {customersList.map(c => (
                                                    <option key={c.id} value={c.name} />
                                                ))}
                                            </datalist>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="date"
                                                value={values.date}
                                                onChange={handleInputChange}
                                                disabled={!isEditable}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Time</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                name="time"
                                                value={values.time}
                                                onChange={handleInputChange}
                                                disabled={!isEditable}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                name="status"
                                                value={values.status}
                                                onChange={handleInputChange}
                                                disabled={!isEditable}
                                            >
                                                <option value="checking">Checking</option>
                                                <option value="ready_for_delivery">Ready for Delivery</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="invoiced">Invoiced</option>
                                            </select>
                                        </div>

                                        <div className="col-12 mt-3">
                                            <label className="form-label fw-semibold">Comments / Notes</label>
                                            <textarea
                                                className="form-control"
                                                name="comments"
                                                rows="3"
                                                placeholder="Enter comments or notes..."
                                                value={values.comments}
                                                onChange={handleInputChange}
                                                disabled={!isEditable}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Items dynamic grid */}
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5>PO Product Items</h5>
                                    {isEditable && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                                            onClick={handleAddRow}
                                        >
                                            <i className="fas fa-plus f-14" /> Add Product Item
                                        </button>
                                    )}
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '50px' }}>#</th>
                                                    <th style={{ width: '150px' }}>Code</th>
                                                    <th>Product <span className="text-danger">*</span></th>
                                                    <th style={{ width: '120px' }} className="text-center">Qty <span className="text-danger">*</span></th>
                                                    <th style={{ width: '120px' }}>Unit</th>
                                                    <th style={{ width: '150px' }} className="text-end">Rate <span className="text-danger">*</span></th>
                                                    <th style={{ width: '180px' }} className="text-end">Amount</th>
                                                    <th style={{ width: '80px' }} className="text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderProducts.length > 0 ? (
                                                    orderProducts.map((row, idx) => (
                                                        <tr key={row.keyId}>
                                                            <td>{idx + 1}</td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={row.code}
                                                                    onChange={(e) => handleRowValChange(idx, 'code', e.target.value)}
                                                                    placeholder="Code"
                                                                    disabled={!isEditable}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    name={`product-${idx}`}
                                                                    list={`products-datalist-${idx}`}
                                                                    placeholder="Select or enter product"
                                                                    value={row.product}
                                                                    onChange={(e) => handleProductChange(idx, e.target.value)}
                                                                    required
                                                                    disabled={!isEditable}
                                                                />
                                                                <datalist id={`products-datalist-${idx}`}>
                                                                    {productsList.map(p => (
                                                                        <option key={p.id} value={p.product_name}>
                                                                            {p.product_code}
                                                                        </option>
                                                                    ))}
                                                                </datalist>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-center"
                                                                    min="0.01"
                                                                    step="any"
                                                                    value={row.qty}
                                                                    onChange={(e) => handleRowValChange(idx, 'qty', e.target.value)}
                                                                    required
                                                                    disabled={!isEditable}
                                                                />
                                                            </td>
                                                            <td>
                                                                {row.packing_unit_options && row.packing_unit_options.includes(',') ? (
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={row.unit}
                                                                        onChange={(e) => handleRowValChange(idx, 'unit', e.target.value)}
                                                                        disabled={!isEditable}
                                                                    >
                                                                        {row.packing_unit_options.split(',').map((u, i) => (
                                                                            <option key={i} value={u.trim()}>{u.trim()}</option>
                                                                        ))}
                                                                    </select>
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        value={row.unit}
                                                                        onChange={(e) => handleRowValChange(idx, 'unit', e.target.value)}
                                                                        placeholder="e.g. Box, Kg"
                                                                        disabled={!isEditable}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    min="0"
                                                                    step="any"
                                                                    value={row.rate}
                                                                    onChange={(e) => handleRowValChange(idx, 'rate', e.target.value)}
                                                                    required
                                                                    disabled={!isEditable}
                                                                />
                                                            </td>
                                                            <td className="text-end fw-bold align-middle">
                                                                Tzs {Number(row.amount).toLocaleString('en-US')}
                                                            </td>
                                                            <td className="text-center">
                                                                {isEditable && orderProducts.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link-danger"
                                                                        onClick={() => handleRemoveRow(idx)}
                                                                        title="Remove row"
                                                                    >
                                                                        <i className="fas fa-trash f-16" />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">
                                                            No products added yet. Click "Add Product Item" above to add products.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            {orderProducts.length > 0 && (
                                                <tfoot>
                                                    <tr className="table-light">
                                                        <td colSpan="6" className="text-end fw-bold">Grand Total:</td>
                                                        <td className="text-end fw-bold text-primary fs-5">
                                                            Tzs {Number(calculateGrandTotal()).toLocaleString('en-US')}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="col-sm-12 text-end mb-5">
                            <Link to="/accounts-dashboard/view-edit" className="btn btn-outline-secondary me-2">
                                {isEditable ? 'Cancel' : 'Back to List'}
                            </Link>
                            {isEditable && (
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save PO Changes'}
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPO;
